import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Lock, User, Truck } from "lucide-react";
import { toast } from "sonner";

// Quick-login chip'leri yalnızca development ortamında gösterilir.
// Production'da gizli kullanıcı bilgisi hiçbir yerde sızmaz.
const QUICK_ACCOUNTS = process.env.NODE_ENV === "production" ? [] : [
  { username: "admin",     password: "admin123",     label: "Yönetici" },
  { username: "personel1", password: "personel123",  label: "Personel" },
  { username: "kurye1",    password: "kurye123",     label: "Kurye 1" },
  { username: "kurye2",    password: "kurye123",     label: "Kurye 2" },
];

function defaultRouteForRole(role) {
  return role === "kurye" ? "/courier" : "/orders";
}

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (user && user !== null) {
    return <Navigate to={defaultRouteForRole(user.role)} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      toast.error("Lütfen kullanıcı adı ve şifre giriniz");
      return;
    }
    setSubmitting(true);
    const res = await login(username.trim().toLowerCase(), password);
    setSubmitting(false);
    if (!res.ok) {
      toast.error(res.error || "Giriş başarısız");
      return;
    }
    toast.success(`Hoş geldiniz, ${res.user.display_name}`);
    navigate(defaultRouteForRole(res.user.role), { replace: true });
  };

  return (
    <div className="min-h-screen w-full grid grid-cols-1 lg:grid-cols-2 bg-white">
      <div
        className="hidden lg:flex relative items-end overflow-hidden border-r-2 border-zinc-200"
        style={{
          backgroundImage:
            "url(https://images.unsplash.com/photo-1608198093002-ad4e005484ec?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-zinc-950/55" />
        <div className="relative z-10 p-12 text-white">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-white text-zinc-950 flex items-center justify-center">
              <Truck strokeWidth={2.5} />
            </div>
            <span className="font-mono text-xs uppercase tracking-[0.3em]">
              Sipariş Takip
            </span>
          </div>
          <h1 className="text-5xl xl:text-6xl font-black tracking-tighter leading-[0.95] mb-6">
            Hızlı<br />sipariş.<br />Zamanında teslim.
          </h1>
          <p className="text-base text-zinc-200 max-w-md leading-relaxed">
            Tek bir akıştan tüm telefon siparişlerini yönet. Geciken teslimat
            kırmızıya döner, sesli uyarı verir — hiçbir sipariş gözden kaçmaz.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="mb-10">
            <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-500 mb-3">
              Giriş Yap
            </div>
            <h2 className="text-3xl sm:text-4xl font-black tracking-tighter text-zinc-950">
              Hesabınıza giriş yapın
            </h2>
            <p className="text-sm text-zinc-500 mt-2">
              Yönetici veya kurye olarak devam edin.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-[0.15em] text-zinc-500 mb-2">
                Kullanıcı Adı
              </label>
              <div className="flex items-center bg-zinc-50 border-2 border-zinc-200 focus-within:border-zinc-950 transition-colors">
                <div className="px-3 text-zinc-400">
                  <User size={18} strokeWidth={2.5} />
                </div>
                <input
                  data-testid="login-username-input"
                  type="text"
                  autoComplete="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="admin / kurye1 / kurye2 / kurye3"
                  className="flex-1 bg-transparent px-1 py-3 text-base text-zinc-900 focus:outline-none placeholder:text-zinc-400"
                />
              </div>
            </div>

            <div>
              <label className="block text-[11px] font-bold uppercase tracking-[0.15em] text-zinc-500 mb-2">
                Şifre
              </label>
              <div className="flex items-center bg-zinc-50 border-2 border-zinc-200 focus-within:border-zinc-950 transition-colors">
                <div className="px-3 text-zinc-400">
                  <Lock size={18} strokeWidth={2.5} />
                </div>
                <input
                  data-testid="login-password-input"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="flex-1 bg-transparent px-1 py-3 text-base text-zinc-900 focus:outline-none placeholder:text-zinc-400"
                />
              </div>
            </div>

            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={submitting}
              className="w-full bg-zinc-950 text-white text-base font-black uppercase tracking-widest py-4 border-b-4 border-black active:border-b-0 active:translate-y-[4px] transition-all disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? "Giriş yapılıyor…" : "Giriş Yap"}
            </button>
          </form>

          <div className="mt-10 pt-6 border-t-2 border-zinc-100">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-3">
              Hızlı Erişim
            </div>
            <div className="grid grid-cols-2 gap-2">
              {QUICK_ACCOUNTS.map((acc) => (
                <button
                  key={acc.username}
                  type="button"
                  data-testid={`quickfill-${acc.username}`}
                  onClick={() => { setUsername(acc.username); setPassword(acc.password); }}
                  className="text-left px-3 py-2 border-2 border-zinc-200 hover:border-zinc-950 transition-colors text-sm font-bold"
                >
                  <div className="text-zinc-950">{acc.label}</div>
                  <div className="font-mono text-[10px] text-zinc-500 mt-0.5">{acc.username}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
